import { Role, UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import {
  Button,
  cn,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  toast,
} from '@utima/ui';
import { Info, Settings } from 'lucide-react';
import { memo, useEffect, useState } from 'react';

import { AutoUpdatePausedBadge, LockedTabLabel } from '@/components';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

import { useDocumentMutation } from '../hooks/useDocumentMutation';
import { formatAutoUpdateCosts } from '../lib/autoUpdateCostCopy';
import { UPDATE_FREQUENCY_OPTIONS } from '../lib/updateFrequencyOptions';

const THRESHOLD_OPTIONS = [
  { value: '0.01', label: '1%' },
  { value: '0.05', label: '5%' },
  { value: '0.1', label: '10%' },
  { value: '0.2', label: '20%' },
] as const;

export const AutoUpdateSettingsModal = memo(function AutoUpdateSettingsModal() {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { hasAccess } = useAccess();
  const { organization } = useCurrentOrganization();
  const { check: checkCost, update: updateCost } = formatAutoUpdateCosts(
    organization?.currency,
  );
  const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();
  const isSuperAdmin = hasAccess([Role.SuperAdmin]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('auto-update');

  const { data, isLoading } = trpc.organizations.getAutoUpdateSettings.useQuery(
    undefined,
    {
      enabled: open,
    },
  );

  const utils = trpc.useUtils();
  const updateMutation =
    trpc.organizations.updateAutoUpdateSettings.useMutation({
      onSuccess: () => {
        utils.organizations.getAutoUpdateSettings.invalidate();
      },
    });

  const docMutations = useDocumentMutation();

  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<UpdateFrequency>(
    UpdateFrequency.Never,
  );
  const [threshold, setThreshold] = useState('0.01');
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (data) {
      setEnabled(data.documentAutoUpdateEnabled);
      setFrequency(data.documentUpdateFrequency);
      setThreshold(String(data.documentChangeThreshold));
    }
  }, [data]);

  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setActiveTab('auto-update');
    }
  }, [open]);

  const handleSave = async () => {
    if (isKbAutoUpdatesLocked) {
      return;
    }

    await updateMutation.mutateAsync({
      documentAutoUpdateEnabled: enabled,
      documentUpdateFrequency: frequency,
      documentChangeThreshold: isSuperAdmin
        ? Number(threshold)
        : (data?.documentChangeThreshold ?? 0.01),
    });

    setOpen(false);
  };

  const handleResetEmbeddings = async () => {
    try {
      await docMutations.resetAllEmbeddingsByOrg.mutateAsync();
      toast.success(t`All embeddings have been queued for re-processing`);
      setConfirmText('');
    } catch {
      toast.error(t`Failed to reset embeddings`);
    }
  };

  const frequencyOptions = UPDATE_FREQUENCY_OPTIONS;

  const tabTriggerClass =
    'px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary';

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button
          variant='ghost'
          outline
          size='icon-sm'
          title={t`Auto-update settings`}
        >
          <Settings className='size-4' />
        </Button>
      </Dialog.Trigger>
      <Dialog.Content className='w-full max-w-md'>
        <Dialog.Title>
          <Trans>Organization Settings</Trans>
        </Dialog.Title>

        <Tabs.Root
          value={activeTab}
          onValueChange={setActiveTab}
          className='flex min-h-0 flex-col'
        >
          <Tabs.List className='border-b border-border'>
            <div className='flex gap-2'>
              <Tabs.Trigger value='auto-update' className={tabTriggerClass}>
                <LockedTabLabel locked={isKbAutoUpdatesLocked}>
                  <Trans>Auto-Update Settings</Trans>
                </LockedTabLabel>
              </Tabs.Trigger>
              {isSuperAdmin && (
                <Tabs.Trigger value='maintenance' className={tabTriggerClass}>
                  <Trans>Maintenance</Trans>
                </Tabs.Trigger>
              )}
            </div>
          </Tabs.List>

          <Tabs.Content value='auto-update' className='py-4'>
            <Dialog.Description className='mb-4'>
              <Trans>
                Configure automatic content updates for website documents.
              </Trans>
            </Dialog.Description>

            <AutoUpdatePausedBadge
              paused={
                isKbAutoUpdatesLocked && frequency !== UpdateFrequency.Never
              }
              className='mb-4'
            />

            {isLoading ? (
              <div className='py-8 text-center text-sm text-gray-500'>
                <Trans>Loading...</Trans>
              </div>
            ) : (
              <div
                className={cn(
                  'flex flex-col gap-5',
                  isKbAutoUpdatesLocked && 'opacity-50',
                )}
              >
                <div className='flex items-center justify-between'>
                  <Label htmlFor='autoUpdateEnabled'>
                    <Trans>Enable auto-updates</Trans>
                  </Label>
                  <Switch
                    id='autoUpdateEnabled'
                    checked={enabled}
                    disabled={isKbAutoUpdatesLocked}
                    onCheckedChange={setEnabled}
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <div className='flex items-center gap-1.5'>
                    <Label htmlFor='updateFrequency'>
                      <Trans>Keep included pages updated</Trans>
                    </Label>
                    <SimpleTooltip
                      title={t`How often to re-check content of pages already in the knowledge base. About ${checkCost} per page check, up to ${updateCost} if the page actually changes.`}
                    >
                      <Info className='size-3.5 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <Select
                    id='updateFrequency'
                    value={frequency}
                    onValueChange={value =>
                      setFrequency(value as UpdateFrequency)
                    }
                    disabled={!enabled || isKbAutoUpdatesLocked}
                  >
                    {frequencyOptions.map(value => (
                      <SelectItem key={value} value={value}>
                        {enumTranslations.updateFrequency[value]}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                {isSuperAdmin && (
                  <div className='flex flex-col gap-2'>
                    <div className='flex items-center gap-1.5'>
                      <Label htmlFor='changeThreshold'>
                        <Trans>Change threshold</Trans>
                      </Label>
                      <SimpleTooltip
                        title={t`Minimum content change percentage required to trigger an update.`}
                      >
                        <Info className='size-3.5 shrink-0 cursor-help text-muted-fg' />
                      </SimpleTooltip>
                    </div>
                    <Select
                      id='changeThreshold'
                      value={threshold}
                      onValueChange={setThreshold}
                      disabled={!enabled || isKbAutoUpdatesLocked}
                    >
                      {THRESHOLD_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                )}
              </div>
            )}

            <Dialog.Footer className='mt-4'>
              <Button
                variant='ghost'
                outline
                onClick={() => setOpen(false)}
                disabled={updateMutation.isPending}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                onClick={handleSave}
                loading={updateMutation.isPending}
                disabled={isLoading || isKbAutoUpdatesLocked}
              >
                <Trans>Save</Trans>
              </Button>
            </Dialog.Footer>
          </Tabs.Content>

          {isSuperAdmin && (
            <Tabs.Content value='maintenance' className='py-4'>
              <div className='flex flex-col gap-4'>
                <div className='rounded-lg border border-danger/30 bg-danger/5 p-4'>
                  <h4 className='mb-2 font-medium text-danger'>
                    <Trans>Reset All Embeddings</Trans>
                  </h4>
                  <p className='mb-4 text-sm text-muted-fg'>
                    <Trans>
                      This will reset all embeddings for every document in this
                      organization. All documents will be queued for
                      re-processing and costs credits. This action cannot be
                      undone.
                    </Trans>
                  </p>
                  <div className='flex flex-col gap-3'>
                    <div className='flex flex-col gap-1.5'>
                      <Label htmlFor='confirmReset' className='text-sm'>
                        <Trans>
                          Type <strong>RESET</strong> to confirm
                        </Trans>
                      </Label>
                      <Input
                        id='confirmReset'
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                        placeholder='RESET'
                      />
                    </div>
                    <Button
                      variant='danger'
                      onClick={handleResetEmbeddings}
                      loading={docMutations.resetAllEmbeddingsByOrg.isPending}
                      disabled={
                        confirmText !== 'RESET' ||
                        docMutations.resetAllEmbeddingsByOrg.isPending
                      }
                    >
                      <Trans>Reset All Embeddings</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            </Tabs.Content>
          )}
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  );
});
